import { TAX_NUMBER_TYPES } from "@tastyworks/tastyworks-api"

function or(...args: string[]) {
  if (args.every((arg) => arg.length === 1)) {
    return `[${args.join()}]`
  } else {
    return "(?:" + args.map((arg) => `(?:${arg})`).join("|") + ")"
  }
}

export const SSN = new RegExp(
  or(
    // Cannot be 000, 666, 9xx
    "[1-578][0-9]{2}",

    "0[1-9]{2}",
    "00[1-9]",
    "0[1-9]0",

    "6[0-57-9]{2}",
    "66[0-57-9]",
    "6[0-57-9]6"
  ) +
    "-?" +
    or(
      // Cannot be 00
      "[1-9][0-9]",
      "[0-9][1-9]"
    ) +
    "-?" +
    or(
      // Cannot be 0000
      "[1-9][0-9]{3}",
      "[0-9][1-9][0-9]{2}",
      "[0-9]{2}[1-9][0-9]",
      "[0-9]{3}[1-9]"
    )
)

// https://www.irs.gov/businesses/small-businesses-self-employed/how-eins-are-assigned-and-valid-ein-prefixes
export const EIN_PREFIXES = [
  "10",
  "12",
  "60",
  "67",
  "50",
  "53",
  "01",
  "02",
  "03",
  "04",
  "05",
  "06",
  "11",
  "13",
  "14",
  "16",
  "21",
  "22",
  "23",
  "25",
  "34",
  "51",
  "52",
  "54",
  "55",
  "56",
  "57",
  "58",
  "59",
  "65",
  "30",
  "32",
  "35",
  "36",
  "37",
  "38",
  "61",
  "15",
  "24",
  "40",
  "44",
  "94",
  "95",
  "80",
  "90",
  "33",
  "39",
  "41",
  "42",
  "43",
  "46",
  "48",
  "62",
  "63",
  "64",
  "66",
  "68",
  "71",
  "72",
  "73",
  "74",
  "75",
  "76",
  "77",
  "82",
  "83",
  "84",
  "85",
  "86",
  "87",
  "88",
  "91",
  "92",
  "93",
  "98",
  "99",
  "20",
  "26",
  "27",
  "45",
  "46",
  "47",
  "81",
  "31",
]

// TODO: uncomment when entity is implemented
const EIN = new RegExp(or(...EIN_PREFIXES) + "-?" + "[0-9]{7}")
// const EIN_PREFIX = new RegExp(or(...EIN_PREFIXES))

export const ITIN = new RegExp(
  "9[0-9]{2}" +
    "-?" +
    or("5[0-9]", "6[0-5]", "7[0-9]", "8[0-8]", "9[0-24-9]") +
    "-?" +
    "[0-9]{4}"
)

/**
 * TODO: entity should be a separate field
 * @param isUsCitizen
 * @param isPermanentResident
 * @returns
 */
export function getCustomerTaxNumber(
  isUsCitizen: boolean,
  isForeigner: boolean
) {
  if (isUsCitizen || !isForeigner) {
    return TAX_NUMBER_TYPES.SSN
  } else {
    return TAX_NUMBER_TYPES.ITIN
  }
}

/* v8 ignore start -- @preserve */
if (import.meta.vitest) {
  const { it, expect, describe } = import.meta.vitest

  describe("getCustomerTaxNumber", () => {
    it("returns ITIN for a non-US citizen with a foreign country of residence", () => {
      expect(getCustomerTaxNumber(false, true)).toBe(TAX_NUMBER_TYPES.ITIN)
    })

    it("returns SSN for a non-US citizen with a US country of residence", () => {
      expect(getCustomerTaxNumber(false, false)).toBe(TAX_NUMBER_TYPES.SSN)
    })

    it("returns SSN for a US citizen with a US country of residence", () => {
      expect(getCustomerTaxNumber(true, false)).toBe(TAX_NUMBER_TYPES.SSN)
    })

    it("returns SSN for a US citizen without a US country of residence", () => {
      expect(getCustomerTaxNumber(true, false)).toBe(TAX_NUMBER_TYPES.SSN)
    })
  })

  describe("SSN", () => {
    it.each([
      { taxNumber: "666-00-0000", expected: false },
      { taxNumber: "900-00-2222", expected: false },
      { taxNumber: "111-11-0000", expected: false },
      { taxNumber: "99-1234567", expected: false },
      { taxNumber: "123-45-6789", expected: true },
      { taxNumber: "372-23-4353", expected: true },
    ])("is $expected for $taxNumber", ({ taxNumber, expected }) => {
      expect(SSN.test(taxNumber)).toBe(expected)
    })
  })

  describe("ITIN", () => {
    it.each([
      { taxNumber: "900-00-2222", expected: false },
      { taxNumber: "99-1234567", expected: false },
      { taxNumber: "911-93-1111", expected: false },
      { taxNumber: "923-70-6789", expected: true },
      { taxNumber: "972-50-4353", expected: true },
      { taxNumber: "911-94-1111", expected: true },
    ])("is $expected for $taxNumber", ({ taxNumber, expected }) => {
      expect(ITIN.test(taxNumber)).toBe(expected)
    })
  })

  describe("EIN", () => {
    it.each([
      { taxNumber: "666-00-0000", expected: false },
      { taxNumber: "923-70-6789", expected: false },
      { taxNumber: "78-112345", expected: false },
      { taxNumber: "99-1234567", expected: true },
      { taxNumber: "01-2345678", expected: true },
    ])("is $expected for $taxNumber", ({ taxNumber, expected }) => {
      expect(EIN.test(taxNumber)).toBe(expected)
    })
  })

  describe("or", () => {
    it("returns correctly when the tax numbers have a length of 1", () => {
      expect(or("6", "7", "8")).toBe("[6,7,8]")
    })

    it("returns correctly when there are multiple arguments", () => {
      expect(or("666-00-0000", "923-70-6789", "78-112345")).toBe(
        "(?:(?:666-00-0000)|(?:923-70-6789)|(?:78-112345))"
      )
    })
  })
}
